/**
 * `/shared` — the web artifacts shared with this person by name.
 *
 * Where an artifact-only account lands (Manage → Share with people): after
 * signing in with no page to return to, from `/`, and from the logo on a
 * shared page's bar. Deliberately OUTSIDE `_app`: that shell calls a dozen
 * endpoints this account is refused, and it has nothing to show them anyway.
 * A member can open it too; it lists what was shared with them by name.
 *
 * `?auto=1` is sign-in's hand-off: with exactly one page shared, go straight
 * to it, so a person with one page never sees a list of one.
 */

import { Wordmark } from "@chai/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { AppWindow, FileText } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

import { useApi } from "@/api";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDocumentTitle } from "@/lib/document-title";

const SearchSchema = z.object({
  auto: z.coerce.number().optional(),
});

export const Route = createFileRoute("/shared")({
  validateSearch: (search) => SearchSchema.parse(search),
  component: SharedWithYouPage,
});

interface SharedArtifact {
  kind: string;
  slug: string;
  title: string | null;
  url: string;
  shared_at: string;
  last_opened_at: string | null;
}

function SharedWithYouPage() {
  const api = useApi();
  const auth = useAuth();
  const { auto } = useSearch({ from: "/shared" });
  useDocumentTitle(["Shared with you"]);

  const signedOut = !auth.isLoading && !auth.me;
  useEffect(() => {
    if (signedOut) window.location.replace(`/signin?returnTo=${encodeURIComponent("/shared")}`);
  }, [signedOut]);

  const pages = useQuery({
    queryKey: ["me-artifacts"],
    enabled: !!auth.me,
    queryFn: () => api.request<SharedArtifact[]>("/me/artifacts"),
  });

  const only = pages.data?.length === 1 ? pages.data[0] : null;
  useEffect(() => {
    if (auto && only) window.location.replace(only.url);
  }, [auto, only]);

  const artifactOnly = !!auth.me?.user.artifact_only;
  const signOut = async () => {
    await auth.logout();
    window.location.href = "/signin";
  };

  return (
    <div className="flex min-h-dvh flex-col bg-[var(--color-surface)]">
      <header className="flex shrink-0 items-center gap-3 border-b border-[var(--color-hairline)] bg-[var(--color-topbar)] px-4 py-2">
        <Wordmark href={artifactOnly ? "/shared" : "/"} />
        <span className="ml-auto truncate text-[12px] text-[var(--color-slate)]">
          {auth.me?.user.email}
        </span>
        {auth.me ? (
          <Button size="sm" variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
        ) : null}
      </header>

      <main className="mx-auto w-full max-w-[720px] flex-1 px-6 py-10">
        <h1 className="text-2xl font-semibold tracking-tight text-[var(--color-ink)]">
          Shared with you
        </h1>
        <p className="mt-1 text-[13px] text-[var(--color-slate)]">
          {artifactOnly
            ? "These are the pages this account can open."
            : "Pages someone shared with you by name."}
        </p>

        <div className="mt-6">
          {auth.isLoading || pages.isLoading || (auto && only) ? (
            <Skeleton className="h-28 w-full rounded-lg" />
          ) : pages.isError ? (
            <p className="text-[13px] text-[var(--color-slate)]">
              Couldn't load your pages. Try again in a moment.
            </p>
          ) : pages.data?.length ? (
            <ul className="flex flex-col gap-2">
              {pages.data.map((p) => (
                <li key={p.slug}>
                  <a
                    href={p.url}
                    className="flex items-center gap-3 rounded-lg border border-[var(--color-hairline)] bg-[var(--color-bg-elevated)] px-4 py-3 no-underline transition hover:border-[var(--color-primary)]"
                  >
                    {p.kind === "app" ? (
                      <AppWindow className="size-4 shrink-0 text-[var(--color-steel)]" aria-hidden />
                    ) : (
                      <FileText className="size-4 shrink-0 text-[var(--color-steel)]" aria-hidden />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-medium text-[var(--color-ink)]">
                        {p.title || p.slug}
                      </span>
                      <span className="block text-[12px] text-[var(--color-slate)]">
                        {p.last_opened_at
                          ? `Opened ${formatDistanceToNow(new Date(p.last_opened_at), { addSuffix: true })}`
                          : `Shared ${formatDistanceToNow(new Date(p.shared_at), { addSuffix: true })}`}
                      </span>
                    </span>
                    <span className="shrink-0 text-[12px] font-medium text-[var(--color-primary)]">
                      Open
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-lg border border-dashed border-[var(--color-hairline)] px-4 py-10 text-center text-[13px] text-[var(--color-slate)]">
              Nothing has been shared with this account yet. Ask the person who
              invited you to share the page again.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
